import { Popover, PopoverInteractionKind } from '@blueprintjs/core'
import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import ReactMarkdown from 'react-markdown'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { translateString } from 'shared-libs/helpers/utils'
import { getEffectiveTranslationTable } from 'shared-libs/components/ui2/fields/modules/ui2-base-field/ui2-base-field.internal'
import type { Label as LabelProps } from 'shared-libs/components/ui2/fields/helpers/ui2-label'
import { NO_LABEL, showsRequirednessMarker } from 'shared-libs/components/ui2/fields/helpers/ui2-label'
import { useFieldShellContext } from 'shared-libs/components/ui2/fields/helpers/ui2-field-shell/ui2-field-shell.context'
import { UI2FormContext } from 'shared-libs/components/ui2/forms/ui2-form/ui2-form.context'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/icon-names'
import './_label.scss'

interface IUI2LabelProps extends LabelProps, Omit<IBaseProps, 'children'> {
  frames?: FramesManager
  translationTable?: any
  textStyleClassName?: string
  /**
   * Suppress the `*` / `(optional)` marker. `UI2Label` fills both from
   * `FieldShellContext`; the shell passes them explicitly since it renders its
   * label outside that provider.
   */
  isReadOnly?: boolean
  isDisabled?: boolean
  /**
   * Where the `(optional)` badge sits. `'inline'` (default) follows the label;
   * `'below'` gives it its own line, used by horizontal layout's narrow label
   * column. The `*` stays inline in both.
   */
  optionalPlacement?: 'inline' | 'below'
}

interface IUI2LabelState {
  isTruncated: boolean
}

class UI2LabelBase extends React.PureComponent<IUI2LabelProps, IUI2LabelState> {
  public static contextType = UI2FormContext
  public declare context: React.ContextType<typeof UI2FormContext>
  public static defaultProps: Partial<IUI2LabelProps> = { maxLines: 2 }

  public state: IUI2LabelState = { isTruncated: false }

  private textRef = React.createRef<HTMLSpanElement>()
  private resizeObserver?: ResizeObserver

  public componentDidMount() {
    this.measureTruncation()
    if (typeof ResizeObserver !== 'undefined' && this.textRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.measureTruncation())
      this.resizeObserver.observe(this.textRef.current)
    }
  }

  public componentDidUpdate(prevProps: IUI2LabelProps) {
    if (
      prevProps.labelText !== this.props.labelText ||
      prevProps.maxLines !== this.props.maxLines ||
      prevProps.translationTable !== this.props.translationTable
    ) {
      this.measureTruncation()
    }
  }

  public componentWillUnmount() {
    this.resizeObserver?.disconnect()
  }

  public render() {
    const { labelText } = this.props
    if (_.isEmpty(labelText) || labelText === NO_LABEL) return null
    return (
      <div className={classNames('ui2-label', this.props.className)}>
        <div className='ui2-label-row' data-testid={labelText + '_Label'}>
          <div className='ui2-label-content'>
            {this.renderText()}
            {this.renderRequiredIndicator('inline')}
          </div>
          {this.renderTooltipButton()}
        </div>
        {this.renderRequiredIndicator('below')}
        {this.renderHelpText()}
      </div>
    )
  }

  private renderText() {
    const { labelText, maxLines } = this.props
    const translated = translateString(labelText!, getEffectiveTranslationTable(this.props))
    const clampStyle: React.CSSProperties = {
      WebkitLineClamp: maxLines,
      ...(maxLines && maxLines > 0
        ? { display: '-webkit-box', WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }
        : null),
    }
    const span = (
      <span
        ref={this.textRef}
        className={classNames('ui2-label-text', this.props.textStyleClassName)}
        style={clampStyle}
      >
        {translated}
      </span>
    )
    if (!this.state.isTruncated) return span
    return (
      <Popover
        interactionKind={PopoverInteractionKind.HOVER}
        hoverOpenDelay={150}
        content={<div className='ui2-label-tooltip'>{translated}</div>}
      >
        {span}
      </Popover>
    )
  }

  private renderRequiredIndicator(slot: 'inline' | 'below') {
    const { isRequired, isOptional, isReadOnly, isDisabled, optionalPlacement } = this.props
    if (!showsRequirednessMarker({ isReadOnly, isDisabled })) return null
    const showsOptional = !!isOptional && !isRequired
    const optionalSlot = optionalPlacement === 'below' ? 'below' : 'inline'
    if (isRequired) {
      return slot === 'inline' ? (
        <span className='ui2-label-requiredType ui2-label-requiredType--required'> *</span>
      ) : null
    }
    if (!showsOptional || slot !== optionalSlot) return null
    return (
      <span className={classNames('ui2-label-requiredType', 'ui2-label-requiredType--optional', {
        'ui2-label-requiredType--ownLine': slot === 'below',
      })}>
        {slot === 'below' ? '(optional)' : ' (optional)'}
      </span>
    )
  }

  private renderHelpText() {
    if (this.context?.inlineHelpText === null) return null
    const { inlineHelpText } = this.props
    const translationTable = getEffectiveTranslationTable(this.props)
    if (!inlineHelpText || _.isEmpty(inlineHelpText)) return null
    const translated = translateString(inlineHelpText, translationTable) ?? inlineHelpText
    return (
      <div className='ui2-label-helpText'>
        <ReactMarkdown>{translated}</ReactMarkdown>
      </div>
    )
  }

  private renderTooltipButton() {
    const { labelText, tooltipText } = this.props
    const translationTable = getEffectiveTranslationTable(this.props)
    if (!tooltipText || _.isEmpty(tooltipText)) return null
    const translated = translateString(tooltipText, translationTable) ?? tooltipText
    return (
      <Popover
        interactionKind={PopoverInteractionKind.CLICK}
        content={<div className='ui2-label-tooltip'><ReactMarkdown>{translated}</ReactMarkdown></div>}
      >
        <button
          aria-label={'More information about ' + (labelText || '')}
          className='ui2-label-tooltipButton'
          data-testid={labelText + '_Tooltip'}
          type='button'
        >
          <UI2Icon name={UI2IconNames.INFO} size={14} />
        </button>
      </Popover>
    )
  }

  private measureTruncation = () => {
    const el = this.textRef.current
    if (!el) return
    const isTruncated = el.scrollHeight - el.clientHeight > 1
    if (isTruncated !== this.state.isTruncated) {
      this.setState({ isTruncated })
    }
  }

}

/**
 * Field label. Defaults read-only / disabled state from `FieldShellContext` so
 * a label inside a field body (switch, checkbox) drops its requiredness marker
 * with the field. `UI2FieldShell` renders its own label outside that provider,
 * so it passes both explicitly.
 */
export function UI2Label(props: IUI2LabelProps) {
  const shell = useFieldShellContext()
  return (
    <UI2LabelBase
      {...props}
      isReadOnly={props.isReadOnly ?? shell.isReadOnly}
      isDisabled={props.isDisabled ?? shell.isDisabled}
    />
  )
}
